import React from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon } from '@patternfly/react-icons';

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  variant?: 'danger' | 'warning' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A reusable confirmation modal component that replaces window.confirm().
 * Provides a consistent UX across the application.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const getButtonVariant = () => {
    switch (variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Modal
      variant={ModalVariant.small}
      isOpen={isOpen}
      onClose={onCancel}
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-body"
    >
      <ModalHeader
        title={title}
        titleIconVariant={variant === 'danger' || variant === 'warning' ? ExclamationTriangleIcon : undefined}
        labelId="confirm-modal-title"
      />
      <ModalBody id="confirm-modal-body">
        {message}
      </ModalBody>
      <ModalFooter>
        <Button
          key="confirm"
          variant={getButtonVariant()}
          onClick={onConfirm}
        >
          {confirmButtonText}
        </Button>
        <Button
          key="cancel"
          variant="link"
          onClick={onCancel}
        >
          {cancelButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
